/**
 * Stable `blob:` URLs for files.
 *
 * Calling `URL.createObjectURL(file)` inline in render mints a brand-new URL on
 * every render. The `<img src>` therefore changes, the browser throws away the
 * decoded image and fetches it again, and the preview visibly reloads. That is
 * unnoticeable on a static page and very noticeable once something re-renders
 * several times per second - like a batch upload publishing progress. It also
 * leaks: every URL created that way is retained for the life of the document.
 *
 * ## Why the URL is created in an effect rather than a memo
 *
 * The app runs under `React.StrictMode`, which deliberately invokes every effect
 * as setup → cleanup → setup to surface exactly this class of bug. A `useMemo`
 * that creates the URL plus an effect that revokes it is broken under that
 * regime: the cleanup revokes the URL, the memo does not re-run because its
 * dependencies did not change, and the component is left holding a revoked URL -
 * a broken image.
 *
 * Creating inside the effect means the second setup produces a fresh, live URL.
 * The cost is that the URL is empty for the first paint, so callers must handle
 * an empty string (render a placeholder) rather than passing it straight to
 * `<img src>`.
 */

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Returns a getter for the stable URL of any file in `files`, or `""` before the
 * URLs exist. The getter's identity changes only when the URLs do, so it is safe
 * to pass to a memoised child.
 */
export const useObjectUrls = (files: readonly File[]) => {
  const [urls, setUrls] = useState<ReadonlyMap<File, string>>(() => new Map());

  // Mirrors `urls` so the reconcile effect can read the current URLs without
  // depending on them (which would make it re-run on its own output).
  const urlsRef = useRef<Map<File, string>>(new Map());

  useEffect(() => {
    const current = urlsRef.current;
    const next = new Map<File, string>();
    let changed = false;

    // Reuse the URL of any file that is still listed, so its preview is not
    // reloaded just because a sibling was added or removed.
    files.forEach((file) => {
      const existing = current.get(file);
      if (existing) {
        next.set(file, existing);
        return;
      }
      next.set(file, URL.createObjectURL(file));
      changed = true;
    });

    current.forEach((url, file) => {
      if (!next.has(file)) {
        URL.revokeObjectURL(url);
        changed = true;
      }
    });

    if (!changed) return;
    urlsRef.current = next;
    setUrls(next);
  }, [files]);

  useEffect(
    () => () => {
      urlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      // Clearing both the ref and the state matters for StrictMode's simulated
      // unmount: the reconcile effect runs again straight afterwards and, seeing
      // an empty cache, mints fresh URLs instead of handing out revoked ones.
      urlsRef.current = new Map();
      setUrls(new Map());
    },
    [],
  );

  return useCallback((file: File): string => urls.get(file) ?? "", [urls]);
};

/**
 * Single-file variant. Returns `""` until the URL exists, so render a placeholder
 * rather than an `<img>` with an empty `src`.
 */
export const useObjectUrl = (file: File | null | undefined): string => {
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (!file) {
      setUrl("");
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return url;
};
